import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type {
  CanvasSyncSisBundle,
  FixSkipReason,
  GetRunResponse,
  SisBundleArtifactName,
  SisBundleInputFile,
  SisBundleStatus,
  SisBundleSubmissionMode,
  SisBundleTrigger,
  UploadSummary,
} from "@canvas-sync/shared";
import {
  SIS_BUNDLE_FIX_FILE,
  SIS_BUNDLE_INPUT_FILES,
  isSisBundleTerminal,
} from "@canvas-sync/shared";
import { api } from "../api/client";
import { ArtifactPreviewModal } from "../components/ArtifactPreviewModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { SisBundleStatusBadge } from "../components/SisBundleStatusBadge";
import { StatusBadge } from "../components/StatusBadge";
import { UploadStatusBadge } from "../components/UploadStatusBadge";
import {
  CanvasResultCard,
  CanvasMessagesCard,
  CanvasStatusChecksCard,
} from "../components/CanvasResultCards";
import {
  formatBytes,
  formatDateTime,
  formatDropId,
  formatDuration,
  formatIdLabel,
  formatRelative,
  formatTime,
} from "../lib/format";
import { triggerLabel, uploadTriggerLabel } from "../lib/labels";
import { useRole } from "../lib/roleContext";
import { POLL, usePoll } from "../lib/usePoll";
import { CANVAS_OK, CANVAS_BAD, CANVAS_IN_FLIGHT } from "../lib/canvasState";
import {
  computeArtifactAvailability,
  type ArtifactAvailability,
} from "../lib/sisBundleArtifacts";

/**
 * Statuses that can be re-triggered. Mirror of `isResubmittable` in
 * services/api/src/handlers/sisBundles/resubmit.ts — keep in sync.
 */
const RESUBMITTABLE: ReadonlySet<SisBundleStatus> = new Set([
  "SUBMITTED",
  "SUBMITTED_SIS_ONLY",
  "SUBMITTED_FIX_ONLY",
  "SKIPPED_UNCHANGED",
  "SKIPPED_GHOST_DETECTION_TIMEOUT",
  "SKIPPED_NO_FIX",
  "DRY_RUN_BUILT",
  "FAILED",
]);

export function SisBundleDetailPage() {
  const params = useParams<{ bundleId: string }>();
  const bundleId = params.bundleId ? decodeURIComponent(params.bundleId) : "";
  const nav = useNavigate();
  const { isAdmin } = useRole();
  const [bundle, setBundle] = useState<CanvasSyncSisBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitErr, setResubmitErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [forceResubmit, setForceResubmit] = useState(false);
  // The CURRENT global submission mode — fetched when the re-trigger confirm
  // opens, because the NEW bundle freezes the live setting, not the source
  // bundle's frozen mode. null = couldn't load (show a generic warning).
  const [currentMode, setCurrentMode] =
    useState<SisBundleSubmissionMode | null>(null);

  function openResubmitConfirm() {
    setCurrentMode(null);
    setConfirmOpen(true);
    api
      .getSettings()
      .then((s) => setCurrentMode(s.sisBundleSubmissionMode ?? "FULL_BUNDLE"))
      .catch(() => setCurrentMode(null));
  }

  usePoll(
    async () => {
      if (!bundleId) return;
      try {
        const b = await api.getSisBundle(bundleId);
        setBundle(b);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
    },
    () =>
      bundle &&
      isSisBundleTerminal(bundle.status) &&
      canvasProcessingSettled(bundle)
        ? POLL.IDLE_MS
        : POLL.ACTIVE_MS,
    [bundleId],
  );

  async function handleResubmit() {
    if (!bundleId) return;
    setResubmitErr(null);
    setResubmitting(true);
    try {
      // `forceResubmit` rides through to the manifest's
      // retrigger.bypassSkipUnchanged so the bundler re-POSTs even when the
      // payload is byte-identical to the last successful submission.
      const result = await api.resubmitSisBundle(bundleId, { forceResubmit });
      setConfirmOpen(false);
      nav(`/sis-bundles/${encodeURIComponent(result.bundleId)}`);
    } catch (e) {
      setResubmitErr((e as Error).message);
    } finally {
      setResubmitting(false);
    }
  }

  if (!bundleId) {
    return <div className="error">Missing bundleId in URL.</div>;
  }

  const canvasImportUrl =
    bundle?.canvasTarget.domain && bundle.sisImportId !== undefined
      ? `${bundle.canvasTarget.domain}/accounts/${bundle.canvasTarget.accountId}/sis_import/${bundle.sisImportId}`
      : null;

  return (
    <section>
      <div className="page-head">
        <div>
          <div className="muted small">
            <Link to="/sis-bundles" className="muted-link">
              All bundles
            </Link>
          </div>
          <h1>
            SIS bundle{" "}
            <span style={{ fontWeight: 400 }} title={bundle?.dropId}>
              {bundle ? formatDropId(bundle.dropId) : "…"}
            </span>
          </h1>
          {bundle && (
            <div className="muted small mono" style={{ marginTop: 4 }}>
              {bundle.bundleId}
            </div>
          )}
        </div>
        {bundle && <SisBundleStatusBadge status={bundle.status} />}
      </div>

      {error && <div className="error">{error}</div>}
      {!bundle && !error && <div className="muted">Loading…</div>}

      {bundle && (
        <>
          {/* Quick badges for the policy flags that aren't a lifecycle
              status of their own. */}
          {(bundle.dryRun ||
            bundle.submissionMode === "GHOST_FIX_ONLY" ||
            bundle.triggeredBy === "manual-retrigger") && (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 4,
              }}
            >
              {bundle.dryRun && (
                <span className="badge badge-cancelled">dry-run</span>
              )}
              {bundle.submissionMode === "GHOST_FIX_ONLY" && (
                <span
                  className="badge badge-queued"
                  title="This bundle sends only the ghost-fix CSV — the 5 SIS files are not sent to Canvas"
                >
                  ghost fix only
                </span>
              )}
              {bundle.triggeredBy === "manual-retrigger" && (
                <span className="badge badge-warn">manual re-trigger</span>
              )}
            </div>
          )}

          {bundle.errorMessage && (
            <div className="error">{bundle.errorMessage}</div>
          )}

          {bundle.missingFiles && (
            <div className="warn-banner">
              <strong>Missing from this drop:</strong>{" "}
              {bundle.missingFiles.map(prettyInputLabel).join(", ")}. PeopleSoft
              didn't send{" "}
              {bundle.missingFiles.length === 1 ? "this file" : "these files"},
              so the bundle was built without{" "}
              {bundle.missingFiles.length === 1 ? "it" : "them"}.
            </div>
          )}

          <BundleLifecycle bundle={bundle} />

          <BundleSummary bundle={bundle} />

          {/* Canvas processing result — same cards the Upload detail uses, so
              the "what Canvas did" story is identical across surfaces. Each
              self-hides when its data is absent (dry-run, skipped, or not yet
              processed), so partial Canvas data degrades gracefully. */}
          <CanvasResultCard
            title="Canvas result"
            counts={bundle.canvasCounts}
            note={canvasResultNote(bundle)}
          />
          <CanvasMessagesCard
            warnings={bundle.canvasWarnings}
            errors={bundle.canvasErrors}
            sample={bundle.canvasProcessingErrorsSample}
            warningsSample={bundle.canvasProcessingWarningsSample}
          />
          <CanvasStatusChecksCard samples={bundle.canvasPollSamples} />

          <RelatedGhostActivity bundle={bundle} />

          <FilesCard bundle={bundle} />

          <FixSection bundle={bundle} />
          <ValidationSection bundle={bundle} />
          <ArtifactsSection bundle={bundle} />

          {/* Raw forensic fields — paths, hashes, lineage, the unprocessed
              Canvas-target record. Kept (nothing removed) but tucked away so
              the page leads with the human-readable story. */}
          <details className="raw-details" style={{ marginTop: 16 }}>
            <summary>Technical details</summary>
            <div style={{ marginTop: 12 }}>
              <SourceSection bundle={bundle} />
              <CanvasSection bundle={bundle} />
              <HashesSection bundle={bundle} />
              <LineageSection bundle={bundle} />
            </div>
          </details>

          <div className="actions" style={{ marginTop: 16 }}>
            {canvasImportUrl && (
              <a
                className="btn"
                href={canvasImportUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Canvas
              </a>
            )}
            {isAdmin &&
              (!RESUBMITTABLE.has(bundle.status) ? (
                <div className="muted small">
                  Re-trigger is only available on terminal bundles that produced
                  an archive. Current status: {bundle.status}.
                </div>
              ) : (
                <>
                  <button
                    className="btn btn-primary"
                    disabled={resubmitting}
                    onClick={openResubmitConfirm}
                  >
                    {resubmitting
                      ? "Re-triggering…"
                      : "Re-trigger from archive"}
                  </button>
                  <label
                    className="muted small"
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <input
                      type="checkbox"
                      checked={forceResubmit}
                      onChange={(e) => setForceResubmit(e.target.checked)}
                    />
                    Force re-submit (bypass skip-unchanged)
                  </label>
                  {resubmitErr && (
                    <div className="error inline">{resubmitErr}</div>
                  )}
                </>
              ))}
          </div>

          {confirmOpen && (
            <ConfirmDialog
              title="Re-trigger SIS bundle"
              message={
                `This will create a new bundle from the archived inputs of ${bundle.bundleId}. ` +
                (forceResubmit
                  ? `Force mode is ON — the bundler will POST to Canvas even if the payload is unchanged. `
                  : `Skip-unchanged is honored — the new bundle will skip the POST if the payload matches the last successful submission. `) +
                // The new bundle freezes the LIVE submission-mode setting, not
                // the mode this (source) bundle was created under — call that
                // out so an operator looking at a fix-only bundle isn't
                // surprised when a re-trigger ships the full SIS drop (or
                // vice versa).
                (currentMode
                  ? `It will send what the current "What gets sent to Canvas" setting says — ${
                      currentMode === "GHOST_FIX_ONLY"
                        ? "ONLY the ghost-fix CSV"
                        : "the full SIS bundle (SIS files + fix)"
                    } — which may differ from what this bundle sent. `
                  : `It will follow the current "What gets sent to Canvas" setting (see the Settings page), which may differ from what this bundle sent. `) +
                `Target Canvas env: ${bundle.env}.`
              }
              confirmPhrase={bundle.bundleId}
              confirmLabel="Re-trigger"
              danger={bundle.env === "prod"}
              busy={resubmitting}
              onConfirm={handleResubmit}
              onCancel={() => setConfirmOpen(false)}
            />
          )}
        </>
      )}
    </section>
  );
}

/* ─── Lifecycle stepper ──────────────────────────────────────────────── */

type StepState = "done" | "active" | "pending" | "failed" | "skipped";

interface Step {
  label: string;
  detail?: string;
  state: StepState;
}

// Canvas workflow_state buckets live in ../lib/canvasState (shared with the
// SIS bundle list so the classification has one source of truth).

/** True once a posted bundle's Canvas import reached a terminal workflow_state
 *  (so the detail page can drop to the slow idle poll). Non-posted bundles are
 *  considered settled — there's nothing to poll. */
function canvasProcessingSettled(b: CanvasSyncSisBundle): boolean {
  if (
    b.status !== "SUBMITTED" &&
    b.status !== "SUBMITTED_SIS_ONLY" &&
    b.status !== "SUBMITTED_FIX_ONLY"
  )
    return true;
  const cw = (b.canvasWorkflowState ?? "").toLowerCase();
  return CANVAS_OK.has(cw) || CANVAS_BAD.has(cw);
}

function BundleLifecycle({ bundle }: { bundle: CanvasSyncSisBundle }) {
  const steps = bundleLifecycleSteps(bundle);
  return (
    <div className="card lifecycle">
      <ol className="lifecycle-steps">
        {steps.map((s, i) => (
          <li key={i} className={`lifecycle-step lifecycle-step-${s.state}`}>
            <span className="lifecycle-marker">
              {renderMarker(s.state, i + 1)}
            </span>
            <div className="lifecycle-text">
              <div className="lifecycle-label">{s.label}</div>
              {s.detail && <div className="lifecycle-detail">{s.detail}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function bundleLifecycleSteps(b: CanvasSyncSisBundle): Step[] {
  const posted =
    b.status === "SUBMITTED" ||
    b.status === "SUBMITTED_SIS_ONLY" ||
    b.status === "SUBMITTED_FIX_ONLY";
  const validationFailed =
    b.status === "INCOMPLETE_INPUTS" || b.status === "INVALID_INPUTS";

  const received: Step = {
    label: "Received",
    detail: formatDateTime(b.createdAt),
    state: "done",
  };

  const validated: Step = {
    label: "Inputs validated",
    detail: validationFailed
      ? (b.errorMessage ?? "Validation failed")
      : `${bundleInputFiles(b).length} SIS files · ${b.validationStatus}` +
        (b.missingFiles
          ? ` · missing ${b.missingFiles.map(prettyInputLabel).join(", ")}`
          : "") +
        (b.enrollmentsBytesMatch === false ? " · input drift" : ""),
    state: validationFailed ? "failed" : "done",
  };

  if (validationFailed) {
    return [
      received,
      validated,
      { label: "Ghost fix resolved", state: "skipped" },
      { label: "Sent to Canvas", state: "skipped" },
      {
        label: "Final result",
        detail: sisStatusBlurb(b.status),
        state: "failed",
      },
    ];
  }

  const waiting = b.status === "WAITING_FOR_GHOST_DETECTION";
  const preDecision = b.status === "RECEIVED" || b.status === "VALIDATING";
  const fixRows = b.fileRowCounts?.[SIS_BUNDLE_FIX_FILE];
  const ghostFix: Step = {
    label: "Ghost fix resolved",
    detail: waiting
      ? `Waiting for ghost detection (attempt ${b.deferCount})`
      : b.fixIncluded
        ? `Fix CSV included${fixRows !== undefined ? ` · ${fixRows} rows` : ""}`
        : b.fixSkipReason
          ? `Skipped: ${fixSkipLabel(b.fixSkipReason)}`
          : "Resolved",
    state: waiting ? "active" : preDecision ? "pending" : "done",
  };

  if (waiting || preDecision) {
    return [
      received,
      validated,
      ghostFix,
      { label: "Sent to Canvas", state: "pending" },
      { label: "Final result", state: "pending" },
    ];
  }

  let sent: Step;
  if (b.status === "DRY_RUN_BUILT") {
    sent = {
      label: "Sent to Canvas",
      detail: "Dry run — built and archived, not sent",
      state: "skipped",
    };
  } else if (b.status === "SKIPPED_UNCHANGED") {
    sent = {
      label: "Sent to Canvas",
      detail: "Unchanged — re-send skipped",
      state: "skipped",
    };
  } else if (b.status === "SKIPPED_GHOST_DETECTION_TIMEOUT") {
    sent = {
      label: "Sent to Canvas",
      detail: "Ghost detection timed out",
      state: "skipped",
    };
  } else if (b.status === "SKIPPED_NO_FIX") {
    sent = {
      label: "Sent to Canvas",
      detail:
        "Nothing sent — ghost-fix-only mode and " +
        (b.fixSkipReason
          ? fixSkipLabel(b.fixSkipReason)
          : "no safe fix to send"),
      state: "skipped",
    };
  } else if (b.status === "FAILED") {
    sent = {
      label: "Sent to Canvas",
      detail: b.errorMessage ?? "Failed at submission",
      state: "failed",
    };
  } else if (b.sisImportId !== undefined) {
    sent = {
      label: "Sent to Canvas",
      detail: `${formatDateTime(b.submittedAt)} · import #${b.sisImportId}`,
      state: "done",
    };
  } else if (b.status === "POSTING") {
    sent = { label: "Sent to Canvas", detail: "Posting…", state: "active" };
  } else {
    sent = { label: "Sent to Canvas", state: "pending" };
  }

  if (!posted) {
    const finalState: StepState =
      b.status === "FAILED"
        ? "failed"
        : isSisBundleTerminal(b.status)
          ? "done"
          : "pending";
    return [
      received,
      validated,
      ghostFix,
      sent,
      {
        label: "Final result",
        detail: sisStatusBlurb(b.status),
        state: finalState,
      },
    ];
  }

  // Posted → Canvas processed the import asynchronously.
  const cw = (b.canvasWorkflowState ?? "").toLowerCase();
  const procDone = CANVAS_OK.has(cw);
  const procFailed = CANVAS_BAD.has(cw);
  const procActive = CANVAS_IN_FLIGHT.has(cw) || cw === "";

  const processing: Step = {
    label: "Canvas processing",
    detail: b.canvasStartedAt
      ? `Started ${formatTime(b.canvasStartedAt)}`
      : cw
        ? `State: ${cw}`
        : "Awaiting Canvas…",
    // Reached a terminal Canvas state ⇒ processing finished (ok OR bad).
    state: procDone || procFailed ? "done" : procActive ? "active" : "pending",
  };

  const finalResult: Step = {
    label: "Final result",
    detail: b.canvasEndedAt
      ? formatDateTime(b.canvasEndedAt)
      : canvasResultBlurb(cw),
    state: procDone ? "done" : procFailed ? "failed" : "pending",
  };

  return [received, validated, ghostFix, sent, processing, finalResult];
}

function renderMarker(state: StepState, n: number) {
  if (state === "done") return n;
  if (state === "failed") return n;
  if (state === "skipped") return "—";
  if (state === "active") return <span className="lifecycle-spinner" />;
  return n;
}

/* ─── Summary meta-grid ──────────────────────────────────────────────── */

function BundleSummary({ bundle: b }: { bundle: CanvasSyncSisBundle }) {
  return (
    <div className="meta-grid">
      <Meta
        label="Started by"
        value={
          <span className="trigger-pill">
            {triggeredByLabel(b.triggeredBy)}
          </span>
        }
      />
      <Meta
        label="Canvas"
        value={<span className={`env-tag env-tag-${b.env}`}>{b.env}</span>}
      />
      <Meta label="Canvas account ID" value={b.canvasTarget.accountId} mono />
      <Meta
        label="Sends to Canvas"
        value={
          b.submissionMode === "GHOST_FIX_ONLY"
            ? "Ghost fix only"
            : "Full SIS bundle"
        }
      />
      <Meta label="Canvas import ID" value={b.sisImportId ?? "—"} mono />
      <Meta
        label="Canvas status"
        value={b.canvasWorkflowState ?? (b.dryRun ? "dry-run" : "—")}
        mono
      />
      <Meta label="Fix file" value={fixSummary(b)} />
      <Meta
        label="Total rows submitted"
        value={b.totalRowsSubmitted ?? "—"}
        mono
      />
      <Meta
        label="Bundle zip size"
        value={
          b.submittedZipBytes !== undefined
            ? formatBytes(b.submittedZipBytes)
            : "—"
        }
        mono
      />
      <Meta label="Created" value={formatDateTime(b.createdAt)} />
      <Meta label="Sent to Canvas" value={formatDateTime(b.submittedAt)} />
      <Meta
        label="Canvas started processing"
        value={formatDateTime(b.canvasStartedAt)}
      />
      <Meta label="Canvas finished" value={formatDateTime(b.canvasEndedAt)} />
      <Meta
        label="Canvas processing time"
        value={formatDuration(b.canvasProcessingDurationMs)}
        mono
      />
      {b.ghostDetectionRunId && (
        <Meta
          label="Ghost-detection sync"
          value={
            <Link
              to={`/syncs/${b.ghostDetectionRunId}`}
              className="muted-link"
              title={b.ghostDetectionRunId}
            >
              open sync
            </Link>
          }
        />
      )}
      {b.sourceBundleId && (
        <Meta
          label="Re-triggered from"
          value={
            <Link
              to={`/sis-bundles/${encodeURIComponent(b.sourceBundleId)}`}
              className="muted-link"
              title={b.sourceBundleId}
            >
              source bundle
            </Link>
          }
        />
      )}
    </div>
  );
}

/* ─── Files included table ───────────────────────────────────────────── */

function FilesCard({ bundle: b }: { bundle: CanvasSyncSisBundle }) {
  // The PS SIS files the manifest listed plus the fix file when it shipped.
  // Row counts come from the bundle build (absent on bundles that never built
  // a zip, e.g. SKIPPED_UNCHANGED) — fall back to "—" so the table stays
  // readable. Ghost-fix-only bundles never put the SIS files in the zip, so
  // the table lists only what was (or would have been) sent.
  const fixOnly = b.submissionMode === "GHOST_FIX_ONLY";
  const sisFiles = bundleInputFiles(b);
  const names: string[] = fixOnly ? [] : [...sisFiles];
  if (b.fixIncluded) names.push(SIS_BUNDLE_FIX_FILE);

  const haveRows = !!b.fileRowCounts;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Files in this bundle</h2>
        <div className="muted small">
          {fixOnly
            ? b.fixIncluded
              ? "Ghost-fix CSV only — the PeopleSoft SIS files were not sent (ghost-fix-only mode)"
              : "Nothing sent — ghost-fix-only mode with no safe fix to send"
            : b.fixIncluded
              ? `${sisFiles.length} PeopleSoft SIS files + the ghost-fix CSV`
              : `${sisFiles.length} PeopleSoft SIS files (no fix CSV shipped)`}
        </div>
      </div>
      <table className="kv-table">
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>File</th>
            <th style={{ textAlign: "right" }}>Rows</th>
            <th style={{ textAlign: "left", paddingLeft: 16 }}>md5</th>
          </tr>
        </thead>
        <tbody>
          {names.map((name) => {
            const isFix = name === SIS_BUNDLE_FIX_FILE;
            const rows = b.fileRowCounts?.[name];
            return (
              <tr key={name}>
                <td className="kv-key">
                  {isFix ? "Ghost fix" : prettyInputLabel(name)}
                  <span className="muted small" style={{ marginLeft: 8 }}>
                    {name}
                  </span>
                </td>
                <td className="kv-val mono" style={{ textAlign: "right" }}>
                  {rows ?? "—"}
                </td>
                <td
                  className="mono muted small"
                  style={{ paddingLeft: 16, wordBreak: "break-all" }}
                >
                  {b.fileHashes[name] ?? "—"}
                </td>
              </tr>
            );
          })}
          {haveRows && (
            <tr>
              <td className="kv-key" style={{ fontWeight: 600 }}>
                Total rows submitted
              </td>
              <td
                className="kv-val mono"
                style={{ textAlign: "right", fontWeight: 600 }}
              >
                {b.totalRowsSubmitted ?? "—"}
              </td>
              <td />
            </tr>
          )}
        </tbody>
      </table>
      {/* When the table is empty (fix-only with nothing to send) the header
          already tells the whole story — no fallback needed. */}
      {!haveRows && names.length > 0 && (
        <div className="muted small" style={{ marginTop: 8 }}>
          {fixOnly
            ? "Row counts aren't recorded for this bundle (the fix-only zip hasn't been built)."
            : "Row counts aren't recorded for this bundle (it didn't build a zip — a skip-unchanged or pre-build terminal state)."}
        </div>
      )}
    </div>
  );
}

/* ─── Related Ghost Canvas activity ──────────────────────────────────── */

/**
 * The connected history for the bundle: the Ghost Canvas detection *sync* that
 * produced this bundle's fix CSV (linked by the stable `ghostDetectionRunId`),
 * plus that sync's standalone Canvas *upload* history (uploads whose
 * `relatedRunId` is that sync).
 *
 * Fans out to the existing `getRun` + `listUploads(runId)` endpoints — the same
 * pattern RunDetail uses for its own "uploads from this sync" panel — rather
 * than embedding the data server-side, keeping the bundle API unchanged.
 *
 * Degrades gracefully: bundles with no `ghostDetectionRunId` (older records,
 * ghost-detection timeouts, or SIS-only ships with no matched sync) show a
 * clear "No linked Ghost Canvas run found" instead of an unsafe guess.
 */
function RelatedGhostActivity({ bundle }: { bundle: CanvasSyncSisBundle }) {
  const runId = bundle.ghostDetectionRunId;
  const [run, setRun] = useState<GetRunResponse | null>(null);
  const [uploads, setUploads] = useState<UploadSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      setRun(null);
      setUploads(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadErr(null);
    // allSettled so a failure on one endpoint doesn't blank the other.
    Promise.allSettled([api.getRun(runId), api.listUploads(50, runId)])
      .then(([runRes, upRes]) => {
        if (cancelled) return;
        if (runRes.status === "fulfilled") setRun(runRes.value);
        else setLoadErr((runRes.reason as Error)?.message ?? "failed to load");
        if (upRes.status === "fulfilled") setUploads(upRes.value.uploads);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Related Ghost Canvas activity</h2>
        {runId && (
          <Link to={`/syncs/${runId}`} className="muted-link" title={runId}>
            open sync
          </Link>
        )}
      </div>

      {!runId ? (
        <div className="muted small">
          No linked Ghost Canvas run found. This bundle didn't record a
          ghost-detection sync — it may pre-date the link (older record), have
          timed out waiting for ghost detection, or shipped SIS-only without a
          matched sync.
        </div>
      ) : (
        <>
          {run ? (
            <ul className="related-uploads">
              <li className="related-upload">
                <StatusBadge status={run.status} />
                <Link
                  to={`/syncs/${run.runId}`}
                  className="run-link"
                  title={run.runId}
                >
                  {formatIdLabel(run.runId)}
                </Link>
                <span className="trigger-pill">
                  {triggerLabel(run.trigger)}
                </span>
                <span className="muted small">
                  Ghosts found:{" "}
                  <span className="mono">{run.counts?.ghostsFound ?? "—"}</span>
                </span>
                <span className="muted small">
                  {formatRelative(run.finishedAt ?? run.queuedAt)}
                </span>
              </li>
            </ul>
          ) : loading ? (
            <div className="muted small">Loading linked sync…</div>
          ) : (
            <div className="muted small">
              Couldn't load the linked sync{loadErr ? ` (${loadErr})` : ""} — it
              may target a different Canvas environment. Open it directly:{" "}
              <Link to={`/syncs/${runId}`} className="muted-link">
                {formatIdLabel(runId)}
              </Link>
              .
            </div>
          )}

          <div className="card-sub" style={{ marginTop: 10 }}>
            <div className="card-sub-head">
              Ghost fix upload history{uploads ? ` · ${uploads.length}` : ""}
            </div>
            {!uploads ? (
              <div className="muted small">{loading ? "Loading…" : "—"}</div>
            ) : uploads.length === 0 ? (
              <div className="muted small">
                {bundle.fixIncluded
                  ? "No standalone Canvas uploads for this sync — its ghost fix shipped inside this SIS bundle."
                  : "No standalone Canvas uploads for this sync."}
              </div>
            ) : (
              <ul className="related-uploads">
                {uploads.map((u) => (
                  <li key={u.uploadId} className="related-upload">
                    <UploadStatusBadge status={u.status} />
                    <Link
                      to={`/uploads/${u.uploadId}`}
                      className="run-link"
                      title={u.uploadId}
                    >
                      {formatIdLabel(u.uploadId)}
                    </Link>
                    <span className="trigger-pill">
                      {uploadTriggerLabel(u.trigger)}
                    </span>
                    <span className="muted small">
                      Canvas import ID:{" "}
                      <span className="mono">{u.sisImportId ?? "—"}</span>
                    </span>
                    <span className="muted small">
                      {formatRelative(u.submittedAt ?? u.queuedAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Labels / blurbs ────────────────────────────────────────────────── */

function triggeredByLabel(t: SisBundleTrigger): string {
  return t === "manual-retrigger" ? "Manual re-trigger" : "Automatic (n8n)";
}

function fixSkipLabel(reason: FixSkipReason): string {
  switch (reason) {
    case "NO_GHOSTS_FOUND":
      return "no ghosts found";
    case "NO_FIX_GENERATED":
      return "no fix generated";
    case "NO_ENROLLMENTS_FILE":
      return "no enrollments file in this drop";
    case "CONTRADICTION":
      return "contradiction with SIS enrollments";
    case "INPUT_DRIFT":
      return "input drift";
    case "GHOST_DETECTION_TIMEOUT":
      return "ghost detection timed out";
  }
}

function fixSummary(b: CanvasSyncSisBundle): string {
  if (b.fixIncluded) return "Included";
  if (b.fixSkipReason) return `Held back (${fixSkipLabel(b.fixSkipReason)})`;
  return "Not included";
}

/** A short note for the Canvas result card header, mirroring the Upload page's
 *  "Canvas accepted every row." affirmation when the import was clean. */
function canvasResultNote(b: CanvasSyncSisBundle): ReactNode {
  const cw = (b.canvasWorkflowState ?? "").toLowerCase();
  if (cw === "imported") return "Canvas accepted every row.";
  return undefined;
}

function sisStatusBlurb(s: SisBundleStatus): string {
  switch (s) {
    case "RECEIVED":
      return "Received — awaiting validation";
    case "VALIDATING":
      return "Validating inputs…";
    case "INCOMPLETE_INPUTS":
      return "Incomplete inputs — bundle not built";
    case "INVALID_INPUTS":
      return "Invalid inputs — bundle not built";
    case "WAITING_FOR_GHOST_DETECTION":
      return "Waiting for ghost detection…";
    case "BUNDLING":
      return "Assembling the bundle…";
    case "POSTING":
      return "Posting to Canvas…";
    case "DRY_RUN_BUILT":
      return "Built and archived (dry-run — not sent)";
    case "SKIPPED_UNCHANGED":
      return "Skipped — payload identical to last successful submission";
    case "SKIPPED_GHOST_DETECTION_TIMEOUT":
      return "Skipped — ghost detection timed out";
    case "SKIPPED_NO_FIX":
      return "Nothing sent — ghost-fix-only mode and no safe fix to send";
    case "SUBMITTED":
      return "Submitted to Canvas with the ghost fix";
    case "SUBMITTED_SIS_ONLY":
      return "Submitted SIS files only (fix held back)";
    case "SUBMITTED_FIX_ONLY":
      return "Submitted the ghost fix only (SIS files not sent)";
    case "FAILED":
      return "Failed";
  }
}

function canvasResultBlurb(cw: string): string {
  switch (cw) {
    case "imported":
      return "Canvas imported the bundle";
    case "imported_with_messages":
      return "Imported with warnings";
    case "failed":
      return "Canvas failed the import";
    case "failed_with_messages":
      return "Some rows failed in Canvas";
    case "aborted":
      return "Canceled in Canvas";
    case "created":
    case "importing":
    case "restoring":
      return "Canvas is processing…";
    default:
      return "Awaiting Canvas result…";
  }
}

/* ─── Kept detail sections (forensics) ───────────────────────────────── */

function Meta({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value: ReactNode | string | number | undefined;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div className="meta">
      <div className="meta-label">{label}</div>
      <div
        className={
          "meta-value" + (mono ? " mono" : "") + (small ? " small" : "")
        }
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "4px 0" }}>
      <div className="muted" style={{ width: 180, flexShrink: 0 }}>
        {label}
      </div>
      <div style={{ flex: 1, wordBreak: "break-all" }}>{children}</div>
    </div>
  );
}

function SourceSection({ bundle }: { bundle: CanvasSyncSisBundle }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Source</h3>
      <Row label="bundleId">
        <code>{bundle.bundleId}</code>
      </Row>
      <Row label="env">{bundle.env}</Row>
      <Row label="dropId">{bundle.dropId}</Row>
      <Row label="psfiles path">
        <code>{bundle.sourcePath}</code>
      </Row>
      <Row label="manifest">
        <code>{bundle.manifestS3Uri}</code>
      </Row>
      <Row label="archive prefix">
        <code>{bundle.archivePrefix}</code>
      </Row>
      <Row label="created">{formatDateTime(bundle.createdAt)}</Row>
      <Row label="updated">{formatDateTime(bundle.updatedAt)}</Row>
      {bundle.ghostDetectionRunId && (
        <Row label="ghost-detection run">
          <Link to={`/syncs/${bundle.ghostDetectionRunId}`}>
            {bundle.ghostDetectionRunId}
          </Link>
        </Row>
      )}
      <Row label="ghost-detection key">
        <code>{bundle.ghostDetectionInputS3Key}</code>
      </Row>
      {bundle.deferCount > 0 && (
        <Row label="defer count">
          {bundle.deferCount}{" "}
          {bundle.lastDeferAt && `(last ${formatDateTime(bundle.lastDeferAt)})`}
        </Row>
      )}
    </div>
  );
}

function ValidationSection({ bundle }: { bundle: CanvasSyncSisBundle }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Validation</h3>
      <Row label="status">{bundle.validationStatus}</Row>
      <Row label="enrollments byte-match">
        {bundle.enrollmentsBytesMatch === undefined
          ? "—"
          : bundle.enrollmentsBytesMatch
            ? "yes"
            : "NO (input drift)"}
      </Row>
      <Row label="artifact">
        <InlineArtifactLink
          bundle={bundle}
          name="validation"
          label="validation.json"
        />
      </Row>
    </div>
  );
}

function FixSection({ bundle }: { bundle: CanvasSyncSisBundle }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Fix file</h3>
      <Row label="included">{bundle.fixIncluded ? "yes" : "no"}</Row>
      {bundle.fixSkipReason && (
        <Row label="skip reason">
          <code>{bundle.fixSkipReason}</code>
        </Row>
      )}
      {bundle.contradictionCount !== undefined &&
        bundle.contradictionCount > 0 && (
          <>
            <Row label="contradictions">{bundle.contradictionCount}</Row>
            {bundle.contradictionSampleS3Uri && (
              <Row label="contradicting rows">
                <InlineArtifactLink
                  bundle={bundle}
                  name="contradiction-sample"
                  label="contradiction-sample.json"
                />
              </Row>
            )}
          </>
        )}
      {bundle.fixFileS3Uri && (
        <Row label="archived fix CSV">
          <InlineArtifactLink
            bundle={bundle}
            name="fix"
            label="SJDC_SR_CANVAS_FIX.csv"
          />
        </Row>
      )}
    </div>
  );
}

function CanvasSection({ bundle }: { bundle: CanvasSyncSisBundle }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Canvas submission</h3>
      <Row label="target env">{bundle.canvasTarget.env}</Row>
      <Row label="target domain">
        <code>{bundle.canvasTarget.domain || "—"}</code>
      </Row>
      <Row label="account id">{bundle.canvasTarget.accountId}</Row>
      {bundle.sisImportId !== undefined && (
        <Row label="sis_import_id">
          <a
            href={`${bundle.canvasTarget.domain}/accounts/${bundle.canvasTarget.accountId}/sis_import`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <code>{bundle.sisImportId}</code>
          </a>
        </Row>
      )}
      {bundle.canvasWorkflowState && (
        <Row label="workflow state">{bundle.canvasWorkflowState}</Row>
      )}
      {bundle.submittedAt && (
        <Row label="submitted at">{formatDateTime(bundle.submittedAt)}</Row>
      )}
      {bundle.submittedZipBytes !== undefined && (
        <Row label="zip size">{formatBytes(bundle.submittedZipBytes)}</Row>
      )}
      {bundle.productionSafetyGuardActive && bundle.env === "prod" && (
        <Row label="safety guard">
          <span className="muted">
            ALLOW_PROD_CANVAS_POST was off at row-creation time
          </span>
        </Row>
      )}
    </div>
  );
}

function HashesSection({ bundle }: { bundle: CanvasSyncSisBundle }) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Hashes</h3>
      <Row label="combined SIS bundle">
        <code>{bundle.combinedSisBundleHash}</code>
      </Row>
      {bundle.finalPayloadHash && (
        <Row label="final payload">
          <code>{bundle.finalPayloadHash}</code>
        </Row>
      )}
      {bundle.lastSuccessfulPayloadHash && (
        <Row label="last successful">
          <code>{bundle.lastSuccessfulPayloadHash}</code>
          {bundle.finalPayloadHash === bundle.lastSuccessfulPayloadHash && (
            <span className="muted" style={{ marginLeft: 8 }}>
              (match — would skip)
            </span>
          )}
        </Row>
      )}
      {bundle.fixFileMd5 && (
        <Row label="fix file md5">
          <code>{bundle.fixFileMd5}</code>
        </Row>
      )}
    </div>
  );
}

function LineageSection({ bundle }: { bundle: CanvasSyncSisBundle }) {
  if (!bundle.sourceBundleId) return null;
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Lineage</h3>
      <Row label="re-triggered from">
        <Link to={`/sis-bundles/${encodeURIComponent(bundle.sourceBundleId)}`}>
          <code>{bundle.sourceBundleId}</code>
        </Link>
      </Row>
      {bundle.bypassSkipUnchanged && (
        <Row label="bypass skip-unchanged">yes (force mode)</Row>
      )}
    </div>
  );
}

// Artifacts grouped by what they are. Each row describes one artifact:
// its API name, display label, filename, and whether the content is
// preview-able in the browser.
interface ArtifactRow {
  /** API artifact name (path segment for the /artifact/{name} endpoint). */
  name: SisBundleArtifactName | SisBundleInputFile;
  /** Display label shown to the user. */
  label: string;
  /** Concrete filename for the download dialog + format detection. */
  filename: string;
  /** Short hint shown under the label. */
  description: string;
  /** True if the content can be rendered in the preview modal
   *  (JSON / CSV / text). Binary artifacts like the zip only get a
   *  download button. */
  previewable: boolean;
}

interface ArtifactGroup {
  title: string;
  description: string;
  rows: ArtifactRow[];
}

function buildArtifactGroups(bundle: CanvasSyncSisBundle): ArtifactGroup[] {
  return [
    {
      title: "Manifest",
      description:
        "The n8n-generated drop manifest — file list, md5s, and the ghost-detection key reference.",
      rows: [
        {
          name: "manifest",
          label: "Manifest",
          filename: "_MANIFEST.json",
          description: "Schema + per-file hashes that triggered this bundle",
          previewable: true,
        },
      ],
    },
    {
      title: "PS SIS inputs",
      description:
        "Byte-identical copies of the PeopleSoft SIS CSVs that n8n delivered for this drop.",
      rows: bundleInputFiles(bundle).map((name) => ({
        name,
        label: prettyInputLabel(name),
        filename: name,
        description: name,
        previewable: true,
      })),
    },
    {
      title: "Validation + fix decision",
      description:
        "Outputs from the bundler's pre-POST checks. Present even when the bundle skipped the fix file.",
      rows: [
        {
          name: "validation",
          label: "Validation result",
          filename: "validation.json",
          description: "Per-file presence + hash recompute outcomes",
          previewable: true,
        },
        {
          name: "fix",
          label: "Worker fix CSV",
          filename: "SJDC_SR_CANVAS_FIX.csv",
          description:
            "Deactivations the worker produced (archived even when skipped)",
          previewable: true,
        },
        {
          name: "contradiction-sample",
          label: "Contradiction sample",
          filename: "contradiction-sample.json",
          description:
            "First N fix rows that conflicted with bundled SIS enrollments",
          previewable: true,
        },
      ],
    },
    {
      title: "Canvas submission",
      description:
        "What we sent to Canvas (or would have sent in dry-run) and what Canvas said back.",
      rows: [
        {
          name: "zip",
          label: "Submitted zip",
          filename: "canvasUpload.zip",
          description: "Exact bytes POSTed to Canvas (binary — download only)",
          previewable: false,
        },
        {
          name: "canvas-response",
          label: "Canvas response",
          filename: "canvas-response.json",
          description: "Raw response body from Canvas SIS Imports endpoint",
          previewable: true,
        },
        {
          name: "result",
          label: "Result summary",
          filename: "result.json",
          description:
            "Final bundle record: sis_import_id, payload hash, status",
          previewable: true,
        },
      ],
    },
  ];
}

/**
 * Compact "filename → download in new tab" link used by the inline rows
 * in Validation/Fix sections. The richer card-style UI is in the
 * Artifacts section below; this is just a quick deep-link for the
 * specific artifact referenced in context.
 *
 * Renders as muted text (no clickable button) when the availability
 * helper says the artifact wasn't produced — same fail-fast UX as the
 * Artifacts cards.
 */
function InlineArtifactLink({
  bundle,
  name,
  label,
}: {
  bundle: CanvasSyncSisBundle;
  name: SisBundleArtifactName | SisBundleInputFile;
  label: string;
}) {
  const availability = computeArtifactAvailability(bundle, name);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!availability.available) {
    return (
      <span className="muted" title={availability.reason}>
        not produced — {availability.reason}
      </span>
    );
  }

  async function open() {
    setErr(null);
    setLoading(true);
    try {
      const { url } = await api.getSisBundleArtifactUrl(
        bundle.bundleId,
        name,
        "attachment",
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  return (
    <span>
      <button
        type="button"
        className="btn btn-link"
        disabled={loading}
        onClick={open}
        style={{ padding: 0, textAlign: "left" }}
      >
        {loading ? "…" : label}
      </button>
      {err && (
        <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
          ({err})
        </span>
      )}
    </span>
  );
}

/** The SIS files the manifest listed, in canonical order. */
function bundleInputFiles(bundle: CanvasSyncSisBundle): SisBundleInputFile[] {
  return SIS_BUNDLE_INPUT_FILES.filter((name) => name in bundle.fileHashes);
}

function prettyInputLabel(name: string): string {
  // 'SJDC_SR_CANVAS_ACCOUNTS.csv' → 'Accounts'
  const m = name.match(/^SJDC_SR_CANVAS_([A-Z]+)\.csv$/);
  if (!m) return name;
  return m[1]!.charAt(0) + m[1]!.slice(1).toLowerCase();
}

function ArtifactsSection({ bundle }: { bundle: CanvasSyncSisBundle }) {
  const groups = buildArtifactGroups(bundle);
  const [preview, setPreview] = useState<{
    title: string;
    filename: string;
    url: string | null;
    artifactName: SisBundleArtifactName | SisBundleInputFile;
  } | null>(null);

  async function openPreview(row: ArtifactRow) {
    setPreview({
      title: row.label,
      filename: row.filename,
      url: null,
      artifactName: row.name,
    });
    try {
      const { url } = await api.getSisBundleArtifactUrl(
        bundle.bundleId,
        row.name,
        "inline",
      );
      setPreview((p) => (p && p.artifactName === row.name ? { ...p, url } : p));
    } catch (e) {
      setPreview((p) =>
        p && p.artifactName === row.name
          ? { ...p, url: `__error__:${(e as Error).message}` }
          : p,
      );
    }
  }

  async function downloadFromPreview() {
    if (!preview) return;
    try {
      const { url } = await api.getSisBundleArtifactUrl(
        bundle.bundleId,
        preview.artifactName,
        "attachment",
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      /* swallow — user can close + retry from the row */
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Artifacts</h3>
      <div className="muted" style={{ marginBottom: 12 }}>
        Files in{" "}
        <code>
          sis-archive/{bundle.env}/{bundle.dropId}/
        </code>
        . The files themselves are permanent — only the download link expires
        (5-min TTL). Clicking again generates a fresh URL.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {groups.map((g) => (
          <ArtifactGroupView
            key={g.title}
            group={g}
            bundle={bundle}
            onPreview={openPreview}
          />
        ))}
      </div>

      {preview && (
        <ArtifactPreviewModal
          title={preview.title}
          filename={preview.filename}
          url={
            preview.url && preview.url.startsWith("__error__:")
              ? null
              : preview.url
          }
          onClose={() => setPreview(null)}
          onDownload={downloadFromPreview}
        />
      )}
    </div>
  );
}

function ArtifactGroupView({
  group,
  bundle,
  onPreview,
}: {
  group: ArtifactGroup;
  bundle: CanvasSyncSisBundle;
  onPreview: (row: ArtifactRow) => void;
}) {
  // Pre-compute availability for every row so the card knows up-front
  // whether the buttons should be enabled. Avoids a flash of "click to
  // download → 404 → 'not produced'" round-trips.
  const rowsWithAvailability = group.rows.map((row) => ({
    row,
    availability: computeArtifactAvailability(bundle, row.name),
  }));

  // Hide groups where ZERO artifacts exist (e.g., hides the Canvas
  // submission group entirely for a SKIPPED_UNCHANGED bundle so the
  // page isn't full of disabled cards).
  const anyAvailable = rowsWithAvailability.some(
    (r) => r.availability.available,
  );

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <strong>{group.title}</strong>
        <div className="muted small">{group.description}</div>
      </div>
      {!anyAvailable && (
        <div
          className="muted small"
          style={{ padding: "8px 12px", fontStyle: "italic" }}
        >
          — none produced for this bundle —
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 8,
        }}
      >
        {rowsWithAvailability.map(({ row, availability }) => (
          <ArtifactCard
            key={row.name}
            row={row}
            bundleId={bundle.bundleId}
            availability={availability}
            onPreview={() => onPreview(row)}
          />
        ))}
      </div>
    </div>
  );
}

function ArtifactCard({
  row,
  bundleId,
  availability,
  onPreview,
}: {
  row: ArtifactRow;
  bundleId: string;
  availability: ArtifactAvailability;
  onPreview: () => void;
}) {
  const [err, setErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  async function download() {
    setErr(null);
    setDownloading(true);
    try {
      const { url } = await api.getSisBundleArtifactUrl(
        bundleId,
        row.name,
        "attachment",
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setDownloading(false);
    }
  }

  const disabled = !availability.available;

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 4,
        padding: 10,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <div style={{ fontWeight: 500 }}>
        {row.label}
        {disabled && (
          <span
            className="badge badge-cancelled"
            style={{ marginLeft: 8, fontSize: 10 }}
          >
            not produced
          </span>
        )}
      </div>
      <div
        className="muted small"
        style={{ wordBreak: "break-all", marginTop: 2 }}
      >
        {disabled ? availability.reason : row.description}
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        {row.previewable && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={onPreview}
            disabled={disabled}
            title={
              disabled
                ? (availability.reason ?? "Not produced for this bundle")
                : "Preview in browser"
            }
          >
            Preview
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm"
          onClick={download}
          disabled={disabled || downloading}
          title={
            disabled
              ? (availability.reason ?? "Not produced for this bundle")
              : "Download to disk"
          }
        >
          {downloading ? "…" : "Download"}
        </button>
        {err && (
          <span className="error inline" style={{ fontSize: 12 }}>
            {err}
          </span>
        )}
      </div>
    </div>
  );
}
